import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ResponsiveContext } from 'grommet';
import { CircleInformation } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { ApiError, ApiErrorItem, ClientService, TCreateClientRequest } from '/src/api';
import { ReferralPartnerService } from '/src/api/services/ReferralPartnerService';
import {
  Box,
  BreadcrumbNav,
  ClientCorporateInformation,
  ClientCountry,
  ClientGeneralInformation,
  ClientProgramCommissions,
  FormCard,
  FormPage,
  Line,
} from '/src/components';
import { ReferralCommissions } from '/src/components/clients/ReferralCommissions';
import { SalesCommissions } from '/src/components/clients/SalesCommissions';
import { config } from '/src/config';
import { useGlobalStore, useUserStore } from '/src/context';
import { ReferralPartner } from '/src/lib/models/ReferralPartner';
import { toastMessages } from '/src/lib/toast';
import { ClientDataForm, CountryId, FormErrors, FormFieldMetadata, FormMetadata } from '/src/lib/types';
import { capitalize, getFormErrors, getIsMobile, getPageTitle } from '/src/utils';

export const CreateClientPage = observer(() => {
  /** Context **/
  const globalStore = useGlobalStore();
  const userStore = useUserStore();
  const navigate = useNavigate();
  const screenSize = useContext(ResponsiveContext);

  /** Refs **/
  const defaultFormData = useRef<ClientDataForm>({
    name: '',
    website: '',
    business_id: '',
    start_date: '',
    first_active_reporting_quarter: '',
    jumpstart_amount: '',
    comments: '',
    hq_address_line1: '',
    hq_address_line2: '',
    hq_address_city: '',
    hq_address_region_id: NaN,
    country_id: NaN,
    hq_address_post_code: '',
    sales: [
      {
        user_id: undefined,
        percent_rate: '',
      },
    ],
    referral: [
      {
        referral_partner_id: undefined,
        percent_rate: '',
      },
    ],
    programs: [],
  });

  /** State **/
  const [isLoading] = useState(false);
  const [referralPartners, setReferralPartners] = useState<ReferralPartner[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Form Values
  const [formValues, setFormValues] = useState<ClientDataForm>(defaultFormData.current);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [commissionErrors, setCommissionErrors] = useState<Record<string, FormErrors>>({});
  const [salesCommissionErrors, setSalesCommissionErrors] = useState<Record<string, FormErrors>>({});
  const [referralCommissionErrors, setReferralCommissionErrors] = useState<Record<string, FormErrors>>({});

  /** Computed **/
  const isMobile = getIsMobile(screenSize);

  const isCanada = formValues.country_id === CountryId.Canada;

  /** Methods **/
  const updateFormValue = (key: string, value: unknown) => {
    setFormValues((prev) => ({
      ...prev,
      [key]: value,
    }));
    if (formErrors[key]) formErrors[key] = '';
  };

  const validateForm = (formValues: ClientDataForm, shouldSetErrors = true) => {
    if (!formValues) return false;

    const formFields: FormMetadata = {
      name: { label: 'Name', max: 100, required: true },
      website: { label: 'Website', max: 255 },
      business_id: { label: 'FEIN or Business Number', required: true, max: 20 },
      start_date: { label: 'Start date', required: true },
      first_active_reporting_quarter: { label: 'First Reporting Period', required: true },
      comments: { label: 'Comments', max: 500 },
      hq_address_line1: { label: 'Address', max: 100, required: true },
      hq_address_line2: { label: 'Address', max: 100 },
      hq_address_city: { label: 'City', max: 50, required: true },
      country_id: { label: 'Country', required: true },
      hq_address_region_id: { label: 'Region', required: true },
      hq_address_post_code: { label: 'Postal code', max: 20, required: true },
    };

    const errors = getFormErrors(formFields, formValues);

    const programErrors: Record<string, FormErrors> = {};
    let hasProgramErrors = false;

    formValues.programs.forEach((programData, i) => {
      const dataErrors = getFormErrors(
        {
          program_id: { label: 'Program', required: true },
          rate: {
            label: 'Commission rate',
            required: true,
            nonZero: true,
            maxValue: programData.is_percent_rate ? 99.99 : 1,
          },
        },
        programData
      );

      if (dataErrors) {
        programErrors[i] = dataErrors;
        hasProgramErrors = true;
      }
    });

    const percentRateField: FormFieldMetadata = {
      label: 'Commission percent',
      required: true,
      nonZero: true,
      maxValue: 100,
    };

    const salesErrors: Record<string, FormErrors> = {};
    let hasSalesErrors = false;
    formValues.sales.forEach((commission, i) => {
      if (commission.user_id === undefined && !commission.percent_rate) return;

      const dataErrors = getFormErrors(
        { user_id: { label: 'Salesperson', required: true }, percent_rate: percentRateField },
        commission
      );
      if (dataErrors) {
        salesErrors[i] = dataErrors;
        hasSalesErrors = true;
      }
    });

    const referralErrors: Record<string, FormErrors> = {};
    let hasReferralErrors = false;
    formValues.referral.forEach((commission, i) => {
      if (commission.referral_partner_id === undefined && !commission.percent_rate) return;

      const dataErrors = getFormErrors(
        { referral_partner_id: { label: 'Referrer', required: true }, percent_rate: percentRateField },
        commission
      );
      if (dataErrors) {
        referralErrors[i] = dataErrors;
        hasReferralErrors = true;
      }
    });

    if (shouldSetErrors) {
      if (errors) setFormErrors(errors);
      if (hasProgramErrors) setCommissionErrors(programErrors);
      setSalesCommissionErrors(salesErrors);
      setReferralCommissionErrors(referralErrors);
    }

    return !errors && !hasProgramErrors && !hasSalesErrors && !hasReferralErrors;
  };

  const createClient = async () => {
    const isValid = validateForm(formValues);
    if (!isValid) {
      toast.error(toastMessages.formValidation.requiredFields);
      return;
    }

    const clientWebsite = formValues.website
      ? formValues.website.split('://').length === 1
        ? `https://${formValues.website}`
        : formValues.website
      : null;

    const filteredSalesCommissions = formValues.sales.filter(
      (commission) => commission.user_id && commission.percent_rate
    );

    const filteredReferralCommissions = formValues.referral.filter(
      (commission) => commission.referral_partner_id && commission.percent_rate
    );

    const totalCommissionRate = [...filteredSalesCommissions, ...filteredReferralCommissions].reduce(
      (sum, commission) => sum + Number(commission.percent_rate),
      0
    );
    if (Math.round(totalCommissionRate * 1e6) / 1e6 > 100) {
      toast.error(toastMessages.formValidation.commissionTotal);
      return;
    }

    const newClient: TCreateClientRequest = {
      name: formValues.name,
      website: clientWebsite,
      business_id: formValues.business_id,
      start_date: formValues.start_date,
      first_active_reporting_quarter: formValues.first_active_reporting_quarter,
      jumpstart_amount: null,
      comments: formValues.comments || null,
      hq_address_line1: formValues.hq_address_line1,
      hq_address_line2: formValues.hq_address_line2 || null,
      hq_address_city: formValues.hq_address_city,
      hq_address_region_id: formValues.hq_address_region_id,
      country_id: formValues.country_id,
      hq_address_post_code: formValues.hq_address_post_code,
      programs: formValues.programs,
      sales_commission_rates: filteredSalesCommissions,
      referral_commission_rates: filteredReferralCommissions,
    };

    try {
      setIsCreating(true);
      const client = await ClientService.create(newClient);
      toast.success(toastMessages.createClient.success);
      navigate(`/clients/${client.id}`);
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[CreateClient] API Error:', apiError.status, apiError.body);

      if (apiError.body?.errors) {
        const errorItems = apiError.body.errors as ApiErrorItem[];

        const errors = { ...formErrors };

        errorItems.forEach((error) => {
          errors[error.field] = capitalize(error.message);
        });

        setFormErrors(errors);
      } else {
        globalStore.handleApiError(apiError, toastMessages.createClient.error);
      }
    } finally {
      setIsCreating(false);
    }
  };

  /** Effects **/
  useEffect(() => {
    document.title = getPageTitle(config.client.basePageTitle, 'Add Client');
    userStore.fetchUsers({ is_internal: true });
    ReferralPartnerService.list()
      .then((partners) => {
        const excludedIds = [1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 13, 14, 15, 35];
        const filteredPartners = partners.filter((partner) => !excludedIds.includes(partner.id));
        setReferralPartners(filteredPartners);
      })
      .catch(console.error);
  }, [userStore]);

  /** Render **/
  return (
    <FormPage
      title="Add Client"
      breadcrumbNav={
        <BreadcrumbNav previousPages={[{ name: 'Clients', link: '/clients' }]} currentPageName={'Add Client'} />
      }
      isLoading={isLoading}
      isDetailPage
    >
      <FormCard
        title="Client Details"
        icon={<CircleInformation size="24px" color="brand" />}
        isLoading={isLoading || isCreating}
        onSubmit={createClient}
      >
        <ClientGeneralInformation
          updateFormValue={updateFormValue}
          formValues={formValues}
          formErrors={formErrors}
          onSubmit={createClient}
          isCanada={isCanada}
        />

        <Line margin="1rem" />

        <Box direction={isMobile ? 'column' : 'row'}>
          <Box width={isMobile ? '100%' : '50%'}>
            <ClientCorporateInformation
              updateFormValue={updateFormValue}
              formValues={formValues}
              formErrors={formErrors}
              onSubmit={createClient}
            />
          </Box>
          {isMobile ? <Line margin="0.5rem" /> : <Line direction="vertical" margin="2rem" />}
          <Box width={isMobile ? '100%' : '50%'}>
            <ClientCountry updateFormValue={updateFormValue} formValues={formValues} formErrors={formErrors} />
          </Box>
        </Box>

        <Line margin="1rem" />

        <ClientProgramCommissions
          countryId={formValues.country_id}
          commissions={formValues.programs}
          setCommissions={(value: unknown) => updateFormValue('programs', value)}
          formErrors={commissionErrors}
        />
        <SalesCommissions
          commissions={formValues.sales}
          setCommissions={(value: unknown) => updateFormValue('sales', value)}
          formErrors={salesCommissionErrors}
          users={userStore.users}
        />
        <ReferralCommissions
          commissions={formValues.referral}
          setCommissions={(value: unknown) => updateFormValue('referral', value)}
          formErrors={referralCommissionErrors}
          referralPartners={referralPartners}
        />
      </FormCard>
    </FormPage>
  );
});
